import { useCallback } from 'react';
import { getDeviceId } from '@amplitude/analytics-browser';
import { airbridge } from '@banhala/utils';
import { useRootRoute } from '../../hooks/useRootNavigation';
import { addWebDidToDeeplink } from '../../utils/addWebDidToDeeplink/addWebDidToDeeplink';

/**
 * 현재 화면을 앱에서 이어 보게 하는 딥링크를 만든다.
 *
 * "앱에서 보기"(모달 버튼)와 상단 앱 유도 배너(자동 redirect 포함)가 같은 목적지를 써야 해서
 * 한 곳으로 모았다. 웹 전용이다 — 이 모듈은 `.web.tsx` 에서만 import 한다.
 */

/** 하단탭 호스트 route 이름 (web `NavigationWithModalProvider` 의 `Stack.Screen name='tab'`). */
const TAB_HOST_ROUTE_NAME = 'tab';

/**
 * 대응 화면이 없을 때의 목적지.
 *
 * 맨 `ably://` 는 쓰지 않는다 — 앱 내 딥링크는 전부 host 가 있고, iOS 는 스킴이 없을 때
 * 기본값으로 이 값을 쓴다(`AppDelegate.swift`). host 없는 형태는 라우팅이 보장되지 않는다.
 */
const APP_HOME_DEEPLINK = 'ably://home';

/** 활성 탭을 특정하지 못했을 때 폴백. 네이티브 블루프린트의 홈 상단탭 딥링크다. */
const DEFAULT_TAB_DEEPLINK = 'ably://home/today';

/**
 * `ably://react/App?screen=…&extras={sno}` 로 열 수 있는 RN 화면들.
 *
 * 그 딥링크는 화면 이름을 앱에 그대로 실어 보내는 형태라, 앱에 같은 이름의 화면이 있고
 * 식별자 하나를 `extras.sno` 로 받는 계약까지 맞는 경우에만 성립한다. 그 밖의 화면(웹뷰 전용
 * 화면·웹에만 있는 화면 등)까지 실어 보내면 앱이 빈 화면을 띄우거나 딥링크가 죽는다.
 */
const APP_SCREEN_DEEPLINK_TARGETS: ReadonlySet<string> = new Set([
  'GoodsDetail',
  'MarketHome',
]);

/**
 * 하단탭 딥링크 `ably://main/:tab_id`.
 *
 * `tab` route 는 params 가 없고 활성 탭이 중첩 state 에만 있어서 route 만으로는 알 수 없다.
 * 그래서 **주소의 첫 세그먼트**를 tab_id 로 쓴다 — 네이티브는 tab_id 를 서버 mainTabs 의
 * `tab.id` 와 매칭하는데(`MainViewModel`), 그 id 는 웹에서 그 탭의 경로 세그먼트와 같은
 * 값이다(`/mypage` ↔ `mypage`). 홈은 `/` 라 세그먼트가 없어 `home` 으로 본다.
 */
const getCurrentTabDeeplink = (): string => {
  if (typeof window === 'undefined') return DEFAULT_TAB_DEEPLINK;
  const [, segment = ''] = window.location.pathname.split('/');
  return `ably://main/${segment === '' ? 'home' : segment}`;
};

/**
 * Screens 는 `extras.sno` 형태가 성립하지 않는다.
 *
 * Screens 가 실제로 필요한 건 `screen_name` 이다(`useGetCommonParams` → `screen_name` /
 * `next_token`). 지면 토큰이 `sno` 로 들어가면 앱이 화면을 못 찾는다.
 */
const buildScreensDeeplink = (screenName: string, nextToken?: string): string =>
  `ably://react/App?screen=Screens&extras=${encodeURIComponent(
    JSON.stringify({ screen_name: screenName, next_token: nextToken }),
  )}`;

type RouteLike = {
  name: string;
  params?: object;
};

/** 현재 route 에 대응하는 앱 딥링크. 대응이 없으면 홈으로 보낸다. */
export const getAppDeeplinkFromRoute = (route: RouteLike): string => {
  const params = (route.params ?? {}) as Record<string, string | undefined>;

  // 탭 화면은 `screen=tab` 으로 열 수 없다 — 그 딥링크는 RN 스택 화면 전용이고, tab route 의
  // params 는 중첩 state 라 아래 extras 추출이 엉뚱한 값을 싣는다.
  if (route.name === TAB_HOST_ROUTE_NAME) {
    return getCurrentTabDeeplink();
  }

  if (route.name === 'WebViewScreen') {
    return params.url
      ? `ably://webview?url=${encodeURIComponent(params.url)}`
      : APP_HOME_DEEPLINK;
  }

  if (route.name === 'EventDetail') {
    return `ably://webview?url=events/${params.eventId}`;
  }

  if (route.name === 'Screens') {
    return params.screen_name
      ? buildScreensDeeplink(params.screen_name, params.next_token)
      : APP_HOME_DEEPLINK;
  }

  if (!APP_SCREEN_DEEPLINK_TARGETS.has(route.name)) {
    return APP_HOME_DEEPLINK;
  }

  // 식별자 하나를 `extras.sno` 로 싣는다. instanceId 는 화면 식별과 무관해 제외한다.
  const [identifier = null] = Object.entries(params)
    .filter(([key]) => key !== 'instanceId')
    .map(([, value]) => value);

  return `ably://react/App?screen=${route.name}${
    identifier
      ? `&extras=${encodeURIComponent(JSON.stringify({ sno: identifier }))}`
      : ''
  }`;
};

const openAppDeeplink = (link: string) => {
  // web device id 를 실어 보내면 앱이 SEND_SYNTHETIC_MERGE 로 웹 비회원과 앱 회원을 한
  // 유저로 통합한다. Airbridge 는 이 딥링크를 그대로 앱에 전달하는 운반책이라(자기 트래킹
  // 파라미터만 덧붙인다), web_did 는 여기서 링크 안에 넣어줘야 한다.
  //
  // SDK 가 아직 초기화되지 않았거나 키가 없으면 getDeviceId() 가 undefined 다. 그때는
  // 병합을 포기하고 원래 딥링크로 보낸다(앱 이동 자체를 막지 않는다).
  const webDid = getDeviceId();
  const finalLink = webDid ? addWebDidToDeeplink({ link, webDid }) : link;

  airbridge.openDeeplink({
    type: 'redirect',
    deeplinks: {
      ios: finalLink,
      android: finalLink,
    },
    fallbacks: {
      ios: 'itunes-appstore',
      android: 'google-play',
    },
  });
};

/**
 * 현재 화면을 앱에서 연다(설치돼 있지 않으면 스토어로).
 *
 * 모달의 "앱에서 보기" 버튼과 배너(클릭·자동 redirect)가 같은 목적지를 쓰도록 이 훅 하나만
 * 쓴다. 호출 측에서 모바일 여부(`ablyUserAgent.isMobile`)를 판단해 부른다.
 */
export const useOpenInApp = (): (() => void) => {
  const route = useRootRoute();
  return useCallback(() => {
    openAppDeeplink(getAppDeeplinkFromRoute(route));
  }, [route]);
};
